import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from "react-query";
import { toast } from "src/components";
import { api } from "src/utils";

export enum CampaignStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  COMPLETED = "COMPLETED",
}

export enum CampaignType {
  COMMISSION = "COMMISSION",
  TURNOVER = "TURNOVER",
}

export const CAMPAIGN_TYPE_LABEL_TH: Record<CampaignType, string> = {
  [CampaignType.COMMISSION]: "COMMISSION (คำนวณคอมมิชชัน)",
  [CampaignType.TURNOVER]: "TURNOVER (นับ turnover เข้า downline)",
};

export enum AddonType {
  REFERRAL_COUNT = "REFERRAL_COUNT",
  SALE_VOLUME = "SALE_VOLUME",
  SEGMENT_BASED = "SEGMENT_BASED",
}

export enum QuestType {
  REFERRAL_COUNT = "REFERRAL_COUNT",
  REFERRAL_STACK = "REFERRAL_STACK",
  REFERRAL_TURNOVER_CONTINUE = "REFERRAL_TURNOVER_CONTINUE",
  REFERRAL_TURNOVER = "REFERRAL_TURNOVER",
  REFERRAL_INVITE_PLAYERS = "REFERRAL_INVITE_PLAYERS",
  REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION = "REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION",
  AFFILIATE_INVITE_CONSECUTIVE_PLAYERS = "AFFILIATE_INVITE_CONSECUTIVE_PLAYERS",
  REFERRAL_TURNOVER_GAME_PROVIDERS = "REFERRAL_TURNOVER_GAME_PROVIDERS",
  REFERRAL_INVITE_DISTINCT_GAME_PROVIDERS = "REFERRAL_INVITE_DISTINCT_GAME_PROVIDERS",
  REFERRAL_INVITE_COMEBACK_PLAYERS = "REFERRAL_INVITE_COMEBACK_PLAYERS",
}

/**
 * Thai-language labels for each QuestType — used in dropdowns, tables and
 * dashboards so users see human-readable names instead of the raw enum strings.
 * The underlying enum value is what's sent to the backend; this map is FE only.
 */
export const QUEST_TYPE_LABEL_TH: Record<QuestType, string> = {
  [QuestType.REFERRAL_COUNT]: "ฝากเงิน x ครั้ง",
  [QuestType.REFERRAL_STACK]: "ฝากเงินซ้ำ x ครั้ง",
  [QuestType.REFERRAL_TURNOVER]: "ยอดเดิมพันสะสมถึง x บาท",
  [QuestType.REFERRAL_TURNOVER_CONTINUE]: "ยอดเดิมพันต่อเนื่อง x วัน จำนวน x คน",
  [QuestType.REFERRAL_INVITE_PLAYERS]: "ชวนคนมาเล่น product_type/ค่ายเกม/เกม x คน",
  [QuestType.REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION]: "ชวนคนมาเล่น product_type/ค่ายเกม/เกม x คน x วัน",
  [QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS]: "ยอดเดิมพันต่อเนื่อง x วัน",
  [QuestType.REFERRAL_TURNOVER_GAME_PROVIDERS]: "ยอดเดิมพันสะสมในค่ายเกม/เกม ถึง x บาท จำนวน x คน",
  [QuestType.REFERRAL_INVITE_DISTINCT_GAME_PROVIDERS]: "ชวนคนมาเล่นเกมต่างกัน x เกม จำนวน x คน",
  [QuestType.REFERRAL_INVITE_COMEBACK_PLAYERS]: "ดึงคนที่หายไปกลับมาเล่น x คน",
};

export const getQuestTypeLabel = (type?: QuestType | string | null): string => {
  if (!type) return "-";
  return QUEST_TYPE_LABEL_TH[type as QuestType] || String(type);
};

/**
 * หน่วยของ `target_value` ต่าง quest type (ดู quest_types.md):
 *   - REFERRAL_TURNOVER → ยอด turnover รวม (บาท)
 *   - AFFILIATE_INVITE_CONSECUTIVE_PLAYERS → จำนวนวันต่อเนื่อง
 *   - quest type อื่น ๆ → จำนวน downline
 */
export const getTargetValueUnit = (type?: QuestType | string | null): string => {
  switch (type) {
    case QuestType.REFERRAL_TURNOVER:
      return "ยอด turnover รวม (บาท)";
    case QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS:
      return "จำนวนวันต่อเนื่อง";
    default:
      return "จำนวน downline";
  }
};

export const getTargetValueLabel = (type?: QuestType | string | null): string =>
  `Target Value (${getTargetValueUnit(type)})`;

/**
 * Default extra_config templates per quest type.
 * Used by the "ใส่ template" button in the condition form to auto-fill the JSON textarea.
 * Keys are kept snake_case to match what the affiliate service reads from the DB
 * (e.g. `condition.extra_config.get("min_per_user", 2)`).
 *
 * Sources (affiliate service):
 *   - pigslot-service/affiliate/src/services/v1/quest/event_driven_quest.py
 *   - pigslot-service/affiliate/src/services/v1/quest/condition_service.py
 */
export const EXTRA_CONFIG_TEMPLATES: Partial<Record<QuestType, Record<string, unknown>>> = {
  // นับจำนวน deposit ของ downline แต่ละคน — completion เมื่อมี downline ที่ deposit ครบ min_per_user
  [QuestType.REFERRAL_STACK]: { min_per_user: 2 },

  // REFERRAL_TURNOVER ไม่มี extra_config — target_value (ระดับ quest) คือยอด turnover รวม (บาท)

  // นับ streak ของ downline — ต้อง deposit ต่อเนื่อง min_streak วัน
  [QuestType.REFERRAL_TURNOVER_CONTINUE]: { min_streak: 2 },

  // นับ downline ที่ลงเดิมพันใน game_provider ที่กำหนด ด้วยยอดเดิมพันต่อครั้ง >= min_bet_amount
  [QuestType.REFERRAL_INVITE_PLAYERS]: {
    game_provider: "PG_GAME",
    min_bet_amount: 0,
  },

  // นับ downline ที่ active ใน game_provider (และ game_id ถ้ากำหนด) >= min_active_days วัน
  // โดยยอดเดิมพันต่อครั้ง >= min_bet_amount
  [QuestType.REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION]: {
    game_provider: "PG_GAME",
    min_bet_amount: 0,
    min_active_days: 1,
  },

  // upline-level streak — นับวันที่มี downline >= min_downlines_per_day คนลงเดิมพัน >= min_bet_amount
  [QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS]: {
    min_bet_amount: 0,
    min_downlines_per_day: 1,
  },

  // นับ downline ที่มี turnover สะสมในค่าย (หรือเกมเฉพาะ) ถึง min_turnover
  [QuestType.REFERRAL_TURNOVER_GAME_PROVIDERS]: {
    game_provider: "PG_GAME",
    min_turnover: 10000,
  },

  // นับ downline ที่เล่นเกม game_id ต่างกันภายใน scope ครบ min_distinct_games เกม
  [QuestType.REFERRAL_INVITE_DISTINCT_GAME_PROVIDERS]: {
    game_provider: "PG_GAME",
    min_distinct_games: 1,
    min_bet_amount: 0,
  },

  // นับ downline ที่กลับมาเล่นหลังหายไป >= inactive_days วัน ด้วยยอดเดิมพันต่อครั้ง >= min_bet_amount
  [QuestType.REFERRAL_INVITE_COMEBACK_PLAYERS]: {
    inactive_days: 30,
    min_bet_amount: 0,
  },
};

/**
 * Field metadata ของ extra_config ต่อ quest type — ใช้ render ฟอร์มแทนการกรอก JSON ดิบ
 * อ้างอิงจาก quest_types.md (key ต้อง snake_case ตรงกับที่ backend อ่าน)
 */
export type ExtraConfigFieldKind = "number" | "text" | "product_type" | "game_provider";

export interface ExtraConfigFieldDef {
  key: string;
  label: string;
  kind: ExtraConfigFieldKind;
  required?: boolean;
  placeholder?: string;
}

export const PRODUCT_TYPE_OPTIONS = [
  { value: "PIG_SPIN", label: "PIG_SPIN" },
  { value: "PIG_CASINO", label: "PIG_CASINO" },
  { value: "PIG_SPORT", label: "PIG_SPORT" },
];

export const GAME_PROVIDER_CODES = [
  "PG_GAME",
  "EVO_GAME",
  "JOKER_GAME",
  "EVOLUTION",
  "NLC_GAME",
  "BTG_GAME",
  "PLAY8_GAME",
  "SPRIBE_GAME",
  "DRAGOONSOFT_GAME",
  "TURBO_GAMES",
  "SEXY",
  "DREAM_GAMING",
  "RELAX_GAME",
  "BG",
  "SA",
  "JILI_GAME",
  "AMBSLOT2_GAME",
  "SPINIX_GAME",
  "PRAGMATIC_SLOT_GAME",
  "PRAGMATIC_LIVE_GAME",
  "MICRO_GAME",
  "YGGDRASIL_GAME",
  "NETENT2_GAME",
  "REDTIGER_GAME",
  "CQ9_GAME",
  "FUNKY_GAME",
  "I8_GAME",
  "MANNA_GAME",
  "FC_GAME",
  "FASTSPIN_GAME",
  "SPADE_GAME",
  "HACK_SAW_GAME",
  "JDB_GAME",
  "GTF_GAME",
  "PNG_GAME",
  "OCTOPLAY_GAME",
  "RTGSLOT_GAME",
  "AMEBA_GAME",
  "ASKMEBET_GAME",
  "AMBPOKER_GAME",
  "ASKMEPLAY_GAME",
  "ACE333_GAME",
  "AMB_5G_GAME",
  "AMB_918KISS_GAME",
  "SEXY_SLOT_GAME",
  "NINJA_GAME",
  "MPY",
  "SBO_SPORTBOOK",
  "SABA",
  "NEXTSPIN_GAME",
  "AMBGAMING_GAME",
  "LIVE22_GAME",
  "ROYAL_GAME",
  "RICH88_GAME",
  "YGR_GAME",
  "BOOONGO_GAME",
  "KAGAME_GAME",
  "AMB_759GAMING_GAME",
  "WAZDAN_GAME",
  "HABANERO_GAME",
  "SIMPLEPLAY_GAME",
  "BOOMING_GAME",
  "FUNTA_GAME",
  "NINESGAME_GAME",
  "GIOCO_GAME",
  "DRAGONGAMING_GAME",
  "GOLDY_GAME",
  "BIGPOT_GAME",
  "EXPANSE_GAME",
  "HOTDOG_GAME",
  "SLOTXO_GAME",
  "UFABET_GAME",
];

export const GAME_PROVIDER_OPTIONS = GAME_PROVIDER_CODES.map((c) => ({ value: c, label: c }));

const FIELD_GAME_PROVIDER: ExtraConfigFieldDef = {
  key: "game_provider",
  label: "ค่ายเกม (game_provider)",
  kind: "game_provider",
  placeholder: "เลือกค่ายเกม",
};
const FIELD_PRODUCT_TYPE: ExtraConfigFieldDef = {
  key: "product_type",
  label: "Product Type",
  kind: "product_type",
};
const FIELD_GAME_ID: ExtraConfigFieldDef = {
  key: "game_id",
  label: "เกม (game_id)",
  kind: "text",
  placeholder: "เช่น PP_sweet_bonanza",
};
const FIELD_MIN_BET: ExtraConfigFieldDef = {
  key: "min_bet_amount",
  label: "ยอดเดิมพันขั้นต่ำ/ครั้ง (min_bet_amount)",
  kind: "number",
};

export const QUEST_EXTRA_CONFIG_FIELDS: Partial<Record<QuestType, ExtraConfigFieldDef[]>> = {
  // REFERRAL_COUNT / REFERRAL_TURNOVER → ไม่มี extra_config
  [QuestType.REFERRAL_STACK]: [
    { key: "min_per_user", label: "จำนวนฝากซ้ำ/คน (min_per_user)", kind: "number", required: true },
  ],
  [QuestType.REFERRAL_TURNOVER_CONTINUE]: [
    { key: "min_streak", label: "จำนวนวันต่อเนื่อง (min_streak)", kind: "number", required: true },
  ],
  [QuestType.REFERRAL_INVITE_PLAYERS]: [FIELD_GAME_PROVIDER, FIELD_PRODUCT_TYPE, FIELD_MIN_BET],
  [QuestType.REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION]: [
    FIELD_GAME_PROVIDER,
    FIELD_PRODUCT_TYPE,
    FIELD_GAME_ID,
    { key: "min_active_days", label: "จำนวนวัน active (min_active_days)", kind: "number", required: true },
    FIELD_MIN_BET,
  ],
  [QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS]: [
    FIELD_MIN_BET,
    { key: "min_downlines_per_day", label: "downline ขั้นต่ำ/วัน (min_downlines_per_day)", kind: "number" },
  ],
  [QuestType.REFERRAL_TURNOVER_GAME_PROVIDERS]: [
    FIELD_GAME_PROVIDER,
    FIELD_PRODUCT_TYPE,
    FIELD_GAME_ID,
    { key: "min_turnover", label: "ยอด turnover สะสม (min_turnover)", kind: "number", required: true },
  ],
  [QuestType.REFERRAL_INVITE_DISTINCT_GAME_PROVIDERS]: [
    FIELD_GAME_PROVIDER,
    FIELD_PRODUCT_TYPE,
    { key: "min_distinct_games", label: "จำนวนเกมต่างกัน (min_distinct_games)", kind: "number", required: true },
    FIELD_MIN_BET,
  ],
  [QuestType.REFERRAL_INVITE_COMEBACK_PLAYERS]: [
    { key: "inactive_days", label: "จำนวนวันที่หายไป (inactive_days)", kind: "number", required: true },
    FIELD_MIN_BET,
    FIELD_GAME_PROVIDER,
    FIELD_PRODUCT_TYPE,
  ],
};

export const getExtraConfigFields = (type?: QuestType | string | null): ExtraConfigFieldDef[] =>
  (type ? QUEST_EXTRA_CONFIG_FIELDS[type as QuestType] : undefined) || [];

/** สร้าง extra_config object จากค่าในฟอร์ม (coerce number, ตัดค่าว่างทิ้ง) */
export const buildExtraConfig = (
  type: QuestType | string | null | undefined,
  extra?: Record<string, unknown>,
): Record<string, unknown> | null => {
  const fields = getExtraConfigFields(type);
  if (!fields.length) return null;
  const out: Record<string, unknown> = {};
  fields.forEach((f) => {
    const v = extra?.[f.key];
    if (f.kind === "number") {
      if (v !== undefined && v !== null && (v as unknown) !== "") out[f.key] = Number(v);
    } else if (typeof v === "string" && v.trim() !== "") {
      out[f.key] = v.trim();
    }
  });
  return Object.keys(out).length ? out : null;
};

export enum RewardType {
  FIXED_MONEY = "FIXED_MONEY",
  BONUS = "BONUS",
  COMMISSION_PERCENTAGE = "COMMISSION_PERCENTAGE",
}

/**
 * Thai-language labels for each RewardType — used in dropdowns, tables and
 * dashboards so users see human-readable names instead of the raw enum strings.
 * The underlying enum value is what's sent to the backend; this map is FE only.
 */
export const REWARD_TYPE_LABEL_TH: Record<RewardType, string> = {
  [RewardType.FIXED_MONEY]: "เงินสด",
  [RewardType.BONUS]: "โบนัส",
  [RewardType.COMMISSION_PERCENTAGE]: "เพิ่ม % คอมมิชชัน",
};

export const getRewardTypeLabel = (type?: RewardType | string | null): string => {
  if (!type) return "-";
  return REWARD_TYPE_LABEL_TH[type as RewardType] || String(type);
};

export interface TierConfigItem {
  tierNumber: number;
  commissionRate: string;
}

export interface AddonConfigItem {
  addonType: AddonType;
  minValue?: number | null;
  maxValue?: number | null;
  limitPerUser?: number | null;
  commissionRate: string;
}

export interface QuestConditionItem {
  // metricType / operator / targetValue were dropped from the condition table —
  // branch dispatch uses quest.questType and progress uses quest.targetValue.
  timeWindowDay?: number | null;
  isUniqueUser?: boolean;
  extraConfig?: Record<string, unknown> | null;
}

export interface QuestBonusItem {
  description?: string;
  durationInHours?: number;
  initialCoinAmount?: number;
  bonusRatio?: number;
  turnoverRatio?: number;
  maximumBonus?: number;
  maximumPerDay?: number;
  convertLimitAmount?: number;
  convertLimitRatio?: number;
}

export interface QuestItem {
  questName: string;
  questDescription?: string;
  questType: QuestType;
  targetValue: number;
  startDate: string;
  endDate: string;
  rewardType: RewardType;
  rewardValue?: string;
  order?: number;
  isActive?: boolean;
  tier?: number | null;
  isAllTier?: boolean;
  conditions: QuestConditionItem[];
  bonuses: QuestBonusItem[];
}

export interface CampaignCreateRequest {
  campaignCode: string;
  campaignName: string;
  description?: string;
  startDate: string;
  endDate: string;
  status?: CampaignStatus;
  type?: CampaignType;
  isActive?: boolean;
  commissionLimit?: string;
  iconUrl?: string;
  tiers: TierConfigItem[];
  addons: AddonConfigItem[];
  quests: QuestItem[];
}

export function useCreateAffiliateCampaign(
  options?: UseMutationOptions<unknown, unknown, CampaignCreateRequest, unknown>,
) {
  return useMutation(
    (request) => api.post("/affiliate/campaign", request),
    {
      onError: () => toast.error("สร้าง campaign ไม่สำเร็จ"),
      onSuccess: () => toast.success("สร้าง campaign สำเร็จ"),
      ...options,
    },
  );
}

export interface AddQuestsToCampaignRequest {
  campaignCode: string;
  quests: QuestItem[];
}

export function useAddQuestsToCampaign(
  options?: UseMutationOptions<unknown, unknown, AddQuestsToCampaignRequest, unknown>,
) {
  return useMutation(
    ({ campaignCode, quests }: AddQuestsToCampaignRequest) =>
      api.post(`/affiliate/campaign/${campaignCode}/quests`, { quests }),
    {
      onError: () => toast.error("เพิ่ม quest ไม่สำเร็จ"),
      onSuccess: () => toast.success("เพิ่ม quest สำเร็จ"),
      ...options,
    },
  );
}

export interface CampaignDetail {
  uid: string;
  campaignCode: string;
  campaignName: string;
  description?: string | null;
  startDate: string;
  endDate: string;
  status: CampaignStatus;
  type?: CampaignType | null;
  iconUrl?: string | null;
  isActive: boolean;
  commissionLimit?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignUpdateRequest {
  campaignName?: string;
  description?: string | null;
  startDate?: string;
  endDate?: string;
  status?: CampaignStatus;
  type?: CampaignType;
  isActive?: boolean;
  commissionLimit?: string | null;
  iconUrl?: string | null;
  tiers?: TierConfigItem[];
  addons?: AddonConfigItem[];
}

export function useUpdateAffiliateCampaign(
  options?: UseMutationOptions<unknown, unknown, { campaignCode: string } & CampaignUpdateRequest, unknown>,
) {
  return useMutation(
    ({ campaignCode, ...body }) => api.patch(`/affiliate/campaign/${campaignCode}`, body),
    {
      onError: () => toast.error("แก้ไข campaign ไม่สำเร็จ"),
      onSuccess: () => toast.success("แก้ไข campaign สำเร็จ"),
      ...options,
    },
  );
}

export interface TierConfigDetail {
  uid: string;
  tierNumber: number;
  commissionRate: number;
}

export interface AddonConfigDetail {
  uid: string;
  addonType: AddonType;
  minValue?: number | null;
  maxValue?: number | null;
  limitPerUser?: number | null;
  commissionRate: number;
}

export interface CampaignDetailResponse {
  campaign: CampaignDetail;
  tierConfig: TierConfigDetail[];
  addonConfig: AddonConfigDetail[];
}

export function useGetAffiliateCampaignDetail(
  campaignCode: string,
  options?: UseQueryOptions<CampaignDetailResponse | null, unknown, CampaignDetailResponse | null, any[]>,
) {
  return useQuery<CampaignDetailResponse | null, unknown, CampaignDetailResponse | null, any[]>(
    ["affiliateCampaignDetail", campaignCode],
    async () => {
      if (!campaignCode) return null;
      const response = await api.get(`/affiliate/campaign/${campaignCode}`);
      return response.data.data as CampaignDetailResponse;
    },
    {
      enabled: !!campaignCode,
      ...options,
    },
  );
}

export interface CampaignListResponse {
  total: number;
  campaigns: CampaignDetail[];
}

export function useListAffiliateCampaigns(
  options?: UseQueryOptions<CampaignListResponse, unknown, CampaignListResponse, any[]>,
) {
  return useQuery<CampaignListResponse, unknown, CampaignListResponse, any[]>(
    ["affiliateCampaignList"],
    async () => {
      const response = await api.get("/affiliate/campaign");
      return response.data.data as CampaignListResponse;
    },
    options,
  );
}

export interface QuestConditionDetail {
  uid?: string;
  timeWindowDay?: number | null;
  isUniqueUser?: boolean;
  extraConfig?: Record<string, unknown> | null;
}

export interface QuestBonusDetail {
  uid?: string;
  description?: string | null;
  durationInHours?: number | null;
  initialCoinAmount?: number | null;
  bonusRatio?: number | null;
  turnoverRatio?: number | null;
  maximumBonus?: number | null;
  maximumPerDay?: number | null;
  convertLimitAmount?: number | null;
  convertLimitRatio?: number | null;
}

export interface QuestDetail {
  uid: string;
  campaignUid: string;
  campaignCode?: string | null;
  campaignName?: string | null;
  questName: string;
  questDescription?: string | null;
  questType: QuestType;
  targetValue: number;
  startDate: string;
  endDate: string;
  rewardType: RewardType;
  rewardValue?: number | null;
  order?: number | null;
  isActive: boolean;
  tier?: number | null;
  isAllTier?: boolean;
  conditions: QuestConditionDetail[];
  bonuses: QuestBonusDetail[];
}

export interface AllQuestsResponse {
  total: number;
  quests: QuestDetail[];
}

export function useGetAllQuests(
  options?: UseQueryOptions<AllQuestsResponse, unknown, AllQuestsResponse, any[]>,
) {
  return useQuery<AllQuestsResponse, unknown, AllQuestsResponse, any[]>(
    ["affiliateAllQuests"],
    async () => {
      const response = await api.get(`/affiliate/campaign/quests`);
      return response.data.data as AllQuestsResponse;
    },
    options,
  );
}

export interface QuestStatusRow {
  label: string;
  value: string;
  ok?: boolean;
}

export interface QuestStatusResponse {
  questUid: string;
  questType: QuestType;
  targetValue: number;
  currentValue: number;
  status: string | null;
  summary: { label: string; value: number | string | null };
  rows: QuestStatusRow[];
}

export function useGetQuestStatus(
  questUid?: string,
  userUid?: string,
  options?: UseQueryOptions<QuestStatusResponse | null, unknown, QuestStatusResponse | null, any[]>,
) {
  return useQuery<QuestStatusResponse | null, unknown, QuestStatusResponse | null, any[]>(
    ["affiliateQuestStatus", questUid || "", userUid || ""],
    async () => {
      if (!questUid || !userUid) return null;
      const response = await api.get(`/affiliate/quest/${questUid}/status`, {
        params: { userUid },
      });
      return response.data.data as QuestStatusResponse;
    },
    {
      enabled: !!questUid && !!userUid,
      ...options,
    },
  );
}

export enum QuestProgressStatus {
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  REWARDED = "REWARDED",
}

/** Thai labels สำหรับสถานะ progress — ใช้แสดงในตาราง/ฟิลเตอร์ (ค่าที่ส่ง backend ยังเป็น enum) */
export const QUEST_PROGRESS_STATUS_LABEL_TH: Record<QuestProgressStatus, string> = {
  [QuestProgressStatus.IN_PROGRESS]: "กำลังดำเนินการ",
  [QuestProgressStatus.COMPLETED]: "สำเร็จ",
  [QuestProgressStatus.REWARDED]: "รับรางวัลแล้ว",
};

export const getQuestProgressStatusLabel = (status?: QuestProgressStatus | string | null): string => {
  if (!status) return "-";
  return QUEST_PROGRESS_STATUS_LABEL_TH[status as QuestProgressStatus] || String(status);
};

export interface QuestProgressItem {
  uid: string;
  campaignUid: string;
  campaignCode: string;
  campaignName: string;
  questUid: string;
  questName: string;
  questType: QuestType;
  rewardType: RewardType;
  rewardValue?: number | null;
  userUid: string;
  currentValue: number;
  targetValue: number;
  percent: number;
  status: QuestProgressStatus;
  completedAt?: string | null;
  rewardedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface QuestProgressResponse {
  total: number;
  page: number;
  pageSize: number;
  userUid: string;
  items: QuestProgressItem[];
}

export interface QuestProgressFilters {
  playerCodeOrPhone: string;
  campaignCode?: string;
  questUid?: string;
  progressStatus?: QuestProgressStatus;
  page?: number;
  pageSize?: number;
}

export function useGetQuestProgress(
  filters: QuestProgressFilters,
  options?: UseQueryOptions<QuestProgressResponse | null, unknown, QuestProgressResponse | null, any[]>,
) {
  const {
    playerCodeOrPhone,
    campaignCode,
    questUid,
    progressStatus,
    page = 1,
    pageSize = 20,
  } = filters;
  return useQuery<QuestProgressResponse | null, unknown, QuestProgressResponse | null, any[]>(
    [
      "affiliateQuestProgress",
      playerCodeOrPhone,
      campaignCode || "",
      questUid || "",
      progressStatus || "",
      page,
      pageSize,
    ],
    async () => {
      if (!playerCodeOrPhone) return null;
      // Only include params that have a value — skip undefined/empty so the URL
      // doesn't contain `progress_status=` when the user leaves it blank.
      const params: Record<string, unknown> = {
        playerCodeOrPhone,
        page,
        pageSize,
      };
      if (campaignCode) params.campaignCode = campaignCode;
      if (questUid) params.questUid = questUid;
      if (progressStatus) params.progressStatus = progressStatus;

      const response = await api.get(`/affiliate/quest-progress`, { params });
      return response.data.data as QuestProgressResponse;
    },
    {
      enabled: !!playerCodeOrPhone,
      ...options,
    },
  );
}

export interface CommissionSearchItem {
  uid: string;
  campaignUid: string;
  campaignCode?: string | null;
  campaignName?: string | null;
  userUid: string;
  downlineUid?: string | null;
  psCode?: string | null;
  phone?: string | null;
  tierNumber: number;
  baseAmount?: number | null;
  tierCommissionRate?: number | null;
  addonCommissionRate?: number | null;
  commissionAmount?: number | null;
  calculationDate?: string | null;
  createdAt?: string | null;
}

export interface CommissionSearchResponse {
  total: number;
  page: number;
  pageSize: number;
  userUid: string;
  items: CommissionSearchItem[];
}

export interface CommissionSearchFilters {
  playerCodeOrPhone: string;
  campaignCode?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export function useSearchAffiliateCommissions(
  filters: CommissionSearchFilters,
  options?: UseQueryOptions<CommissionSearchResponse | null, unknown, CommissionSearchResponse | null, any[]>,
) {
  const {
    playerCodeOrPhone,
    campaignCode,
    startDate,
    endDate,
    page = 1,
    pageSize = 20,
  } = filters;
  return useQuery<CommissionSearchResponse | null, unknown, CommissionSearchResponse | null, any[]>(
    [
      "affiliateCommissionSearch",
      playerCodeOrPhone,
      campaignCode || "",
      startDate || "",
      endDate || "",
      page,
      pageSize,
    ],
    async () => {
      if (!playerCodeOrPhone) return null;
      // Only include params that have a value so blank filters don't appear in the URL.
      const params: Record<string, unknown> = { playerCodeOrPhone, page, pageSize };
      if (campaignCode) params.campaignCode = campaignCode;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get(`/affiliate/commission/search`, { params });
      return response.data.data as CommissionSearchResponse;
    },
    {
      enabled: !!playerCodeOrPhone,
      ...options,
    },
  );
}

export interface ProgressLogEntry {
  uid: string;
  progressId?: string | null;
  campaignUid: string;
  uplineUserUid: string;
  downlineUserUid: string;
  questUid: string;
  oldValue: number;
  newValue: number;
  delta: number;
  oldStatus?: QuestProgressStatus | null;
  newStatus?: QuestProgressStatus | null;
  actionType: string;
  updatedBy?: string | null;
  createdAt: string;
  psCode?: string | null;
  phone?: string | null;
}

export interface ProgressLogResponse {
  progressUid: string;
  total: number;
  items: ProgressLogEntry[];
}

export function useGetProgressLog(
  progressUid: string,
  options?: UseQueryOptions<ProgressLogResponse | null, unknown, ProgressLogResponse | null, any[]>,
) {
  return useQuery<ProgressLogResponse | null, unknown, ProgressLogResponse | null, any[]>(
    ["affiliateProgressLog", progressUid],
    async () => {
      if (!progressUid) return null;
      const response = await api.get(`/affiliate/quest-progress/${progressUid}/log`);
      return response.data.data as ProgressLogResponse;
    },
    {
      enabled: !!progressUid,
      ...options,
    },
  );
}

export interface DailyParticipantPoint {
  date: string;
  count: number;
}

export interface CampaignDashboardResponse {
  activeCampaigns: number;
  participantsToday: number;
  completedToday: number;
  rewardPaidToday: number;
  days: number;
  dailyParticipants: DailyParticipantPoint[];
}

export function useAffiliateCampaignDashboard(
  days: number = 7,
  options?: UseQueryOptions<CampaignDashboardResponse, unknown, CampaignDashboardResponse, any[]>,
) {
  return useQuery<CampaignDashboardResponse, unknown, CampaignDashboardResponse, any[]>(
    ["affiliateCampaignDashboard", days],
    async () => {
      const response = await api.get(`/affiliate/dashboard`, { params: { days } });
      return response.data.data as CampaignDashboardResponse;
    },
    options,
  );
}

export interface QuestPipelineResponse {
  inProgress: number;
  completed: number;
  rewarded: number;
  total: number;
}

export function useQuestPipeline(
  options?: UseQueryOptions<QuestPipelineResponse, unknown, QuestPipelineResponse, any[]>,
) {
  return useQuery<QuestPipelineResponse, unknown, QuestPipelineResponse, any[]>(
    ["affiliateQuestPipeline"],
    async () => {
      const response = await api.get(`/affiliate/quest-pipeline`);
      return response.data.data as QuestPipelineResponse;
    },
    options,
  );
}
